import { z } from 'zod';
/**
 * Domain Zod schemas for API validation
 *
 * These schemas validate API request and response payloads.
 * Used by both backend (validation) and frontend (type inference).
 */
/**
 * Domain response schema
 */
export const domainSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    workspaceUuid: z.string().uuid(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Domain with projects response schema
 */
export const domainWithProjectsSchema = domainSchema.extend({
    projects: z.array(z.object({
        uuid: z.string().uuid(),
        name: z.string(),
        description: z.string().nullable(),
    })),
});
/**
 * Create domain request schema
 *
 * Domain creation requires assigning at least one domain admin.
 * Each UUID must belong to an active workspace member (enforced server-side).
 */
export const createDomainRequestSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(100, 'Name must be 100 characters or less')
        .trim(),
    description: z.string().max(500, 'Description must be 500 characters or less').trim().optional(),
    domainAdminUuids: z
        .array(z.string().uuid('Invalid user UUID - must be a valid workspace member'))
        .min(1, 'At least one domain administrator is required'),
});
/**
 * Update domain request schema
 *
 * When domainAdminUuids is provided it replaces the full admin list:
 * new UUIDs are assigned, removed UUIDs are revoked.
 */
export const updateDomainRequestSchema = z.object({
    name: z
        .string()
        .min(1, 'Name is required')
        .max(100, 'Name must be 100 characters or less')
        .trim()
        .optional(),
    description: z.string().max(500, 'Description must be 500 characters or less').trim().optional(),
    domainAdminUuids: z
        .array(z.string().uuid('Invalid user UUID - must be a valid workspace member'))
        .optional(),
});
/**
 * Assign domain member request schema
 *
 * Used by workspace admin (any role) or domain admin (any role) to assign a domain-level role.
 * The userUuid must belong to an active workspace member (enforced server-side).
 */
export const assignDomainAdminRequestSchema = z.object({
    userUuid: z.string().uuid('Invalid user UUID'),
});
/**
 * Assign domain role request schema
 *
 * General role assignment for domain-level access.
 * Used by workspace admins or domain admins to assign any domain role.
 */
export const assignDomainRoleRequestSchema = z.object({
    userUuid: z.string().uuid('Invalid user UUID'),
    role: z.enum(['admin', 'manager', 'viewer', 'collaborator'], {
        errorMap: () => ({ message: 'Role must be one of: admin, manager, viewer, collaborator' }),
    }),
});
/**
 * List domains response schema
 */
export const listDomainsResponseSchema = z.object({
    domains: z.array(domainSchema),
});
/**
 * Create domain response schema
 */
export const createDomainResponseSchema = z.object({
    domain: domainSchema,
});
/**
 * Update domain response schema
 */
export const updateDomainResponseSchema = z.object({
    domain: domainSchema,
});
//# sourceMappingURL=domain.js.map